import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Col, Empty, Row, Typography } from 'antd';
import Chart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';

import { PlayerAnalysisType, selectors } from 'state';

const { Text } = Typography;

export type Props = {
  player: PlayerAnalysisType;
  assetClassNumber: number;
};

const CHART_HEIGHT_PX = 420;

function formatMillions(v: number) {
  const sign = v < 0 ? '-' : '';
  const abs = Math.abs(v);

  if (abs >= 1_000_000_000)
    return `${sign}${(abs / 1_000_000_000).toFixed(0)}B`;
  if (abs >= 1_000_000) return `${sign}${(abs / 1_000_000).toFixed(0)}M`;
  if (abs >= 1_000) return `${sign}${(abs / 1_000).toFixed(0)}K`;
  return `${sign}${abs.toFixed(0)}`;
}

function truncateLabel(s: string, max = 18) {
  if (s.length <= max) return s;
  return `${s.slice(0, max - 3)}...`;
}

const PerInstrumentPnLDistributionTab: React.FC<Props> = ({
  player,
  assetClassNumber,
}) => {
  const session = useSelector(selectors.activeSession);
  const repCcy = useSelector(
    selectors.currencyById(session.reporting_currency)
  );

  const playerDistributionPerInstrument = useSelector(
    selectors.perAssetClassPnLByPlayerId(player.player_id, assetClassNumber)
  );

  const { categories, values, totalPnl, minVal, maxVal } = useMemo(() => {
    const dist = playerDistributionPerInstrument ?? [];

    const items = dist.map((d: any) => ({
      name: String(d.name ?? ''),
      y: Math.round(d.reporting_pnl ?? 0),
    }));

    const cats = items.map(i => i.name);
    const vals = items.map(i => i.y);
    const total = vals.reduce((acc, v) => acc + v, 0);

    const minV = vals.length ? Math.min(...vals) : 0;
    const maxV = vals.length ? Math.max(...vals) : 0;

    return {
      categories: cats,
      values: vals,
      totalPnl: total,
      minVal: minV,
      maxVal: maxV,
    };
  }, [playerDistributionPerInstrument]);

  const hasData = (playerDistributionPerInstrument?.length ?? 0) > 0;

  const formattedTotal = useMemo(() => {
    return `${Math.round(totalPnl).toLocaleString('en-US', {
      maximumFractionDigits: 0,
    })} ${repCcy.symbol}`;
  }, [totalPnl, repCcy.symbol]);

  const series = useMemo(() => [{ name: ' ', data: values }], [values]);

  const options = useMemo((): ApexOptions => {
    const span = Math.max(Math.abs(minVal), Math.abs(maxVal));
    const pad = Math.max(span * 0.1, 1);

    // Include zero and pad like Highcharts
    const xMin = Math.min(0, minVal) - pad;
    const xMax = Math.max(0, maxVal) + pad;

    return {
      chart: {
        type: 'bar',
        height: CHART_HEIGHT_PX,
        toolbar: { show: false },
        animations: { enabled: false },
      },
      title: { text: 'PnL Distribution' },
      plotOptions: {
        bar: {
          horizontal: true,
          distributed: true, // colorByPoint: true
          barHeight: '70%',
        },
      },
      legend: { show: false },
      dataLabels: { enabled: false },

      // ✅ categories MUST be here (Apex will display them on Y when horizontal=true)
      xaxis: {
        categories: categories.map(c => truncateLabel(c, 22)),
        min: xMin,
        max: xMax,
        tickAmount: 6,
        title: {
          text: `${repCcy.symbol} value | (Total PnL: ${formattedTotal})`,
        },
        labels: {
          // ✅ TS expects (value: string, timestamp?) => string|string[]
          formatter: (value: string) => {
            const n = Number(value);
            if (!Number.isFinite(n)) return value;
            return formatMillions(n);
          },
        },
      },

      yaxis: {
        // show full names in tooltip; axis already truncated via categories above
        labels: {
          style: { fontSize: '12px' },
        },
      },

      tooltip: {
        y: {
          formatter: (val: number) =>
            `${Math.round(val).toLocaleString('en-US', {
              maximumFractionDigits: 0,
            })} ${repCcy.symbol}`,
          title: { formatter: () => '' },
        },
      },

      // grid: { padding: { left: 12, right: 12 } },
      grid: {
        show: true,
        xaxis: {
          lines: {
            show: true, // ✅ vertical grid lines
          },
        },
        yaxis: {
          lines: {
            show: false, // ❌ no horizontal grid lines
          },
        },
        padding: { left: 12, right: 12 },
      },
      annotations: {
        xaxis: [
          {
            x: 0,
            borderColor: '#555', // visible but not too strong
            strokeDashArray: 0, // solid line
            // label: {
            //   show: false, // no label, just the line
            // },
          },
        ],
      },
    };
  }, [categories, repCcy.symbol, formattedTotal, minVal, maxVal]);

  if (!hasData) {
    return (
      <Empty
        description={
          <Text type="secondary">No PnL distribution available yet.</Text>
        }
      />
    );
  }

  return (
    <div style={{ width: '100%' }}>
      <Row>
        <Col span={19}>
          <div style={{ width: 400 }}>
            <Chart
              options={options}
              series={series as any}
              type="bar"
              height={CHART_HEIGHT_PX}
            />
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default PerInstrumentPnLDistributionTab;
